import { useLinkTo } from '@react-navigation/native';
import {
  alreadyRedirectedOnAppStartAtom,
  isLoggedInAtom,
  isStartingAtom,
  lastNotificationResponseAtom,
} from 'app/atoms';
import { notificationPath } from 'app/lib/notification-link';
import { getNotificationLink } from 'app/lib/notifications';
import { navigationRef, subscribeNavigationState } from 'app/lib/root-navigation';
import { CustomerIO } from 'customerio-reactnative';
import * as Notifications from 'expo-notifications';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useEffectEvent, useRef, useSyncExternalStore } from 'react';

export function NotificationHandler() {
  const linkTo = useLinkTo();
  const setLastNotificationResponse = useSetAtom(lastNotificationResponseAtom);
  const setAlreadyRedirectedOnAppStart = useSetAtom(alreadyRedirectedOnAppStartAtom);
  const lastNotificationResponse = useAtomValue(lastNotificationResponseAtom);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isStarting = useAtomValue(isStartingAtom);
  const lastHandledResponse = useRef<string | null>(null);
  const isNavigationReady = useSyncExternalStore(
    subscribeNavigationState,
    () => navigationRef.isReady(),
    () => false,
  );

  const handleNotificationResponse = useEffectEvent((response: Notifications.NotificationResponse) => {
    const request = response.notification.request;
    const responseKey = JSON.stringify([request.identifier, response.actionIdentifier]);
    setLastNotificationResponse(null);
    // Expo's last-response hook and the live listener can deliver the same tap.
    if (lastHandledResponse.current === responseKey) return;
    lastHandledResponse.current = responseKey;

    const pushNotification = request.content;
    const pushLink =
      notificationPath(pushNotification.data?.link) ??
      notificationPath(getNotificationLink(pushNotification.title, pushNotification.body));

    if (pushLink) {
      setAlreadyRedirectedOnAppStart(true);
      linkTo(pushLink);
    }

    if (request.trigger && 'type' in request.trigger && request.trigger.type === 'push') {
      const cioPayload = request.trigger.payload;
      if (cioPayload) CustomerIO.pushMessaging.trackNotificationResponseReceived(cioPayload);
    }
  });

  useEffect(() => {
    // Keep listening after a cold-start response has been consumed.
    const subscription = Notifications.addNotificationResponseReceivedListener(setLastNotificationResponse);
    return () => subscription.remove();
  }, [setLastNotificationResponse]);

  useEffect(() => {
    // App.tsx publishes the launch response asynchronously; navigation may also become ready later.
    if (lastNotificationResponse && isNavigationReady && isLoggedIn && !isStarting) {
      handleNotificationResponse(lastNotificationResponse);
    }
  }, [lastNotificationResponse, isNavigationReady, isLoggedIn, isStarting]);

  return null;
}
